import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { Box, useTheme, useMediaQuery, alpha } from '@mui/material';
import { format, parseISO, subMonths, eachMonthOfInterval } from 'date-fns';
import { ja } from 'date-fns/locale';
import { motion } from 'framer-motion';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface RevenueChartProps {
  treatmentRecords: Array<{
    id: string;
    treatmentDate: string;
    totalAmount: number;
    treatmentMenu: string;
  }>;
  height?: number;
}

export const RevenueChart: React.FC<RevenueChartProps> = ({ 
  treatmentRecords,
  height 
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const chartHeight = height || (isMobile ? 200 : 300);

  const chartData = useMemo(() => {
    // 過去6ヶ月のデータを生成
    const now = new Date();
    const startDate = subMonths(now, 5);
    const months = eachMonthOfInterval({
      start: startDate,
      end: now
    });

    // カテゴリー別の月次売上を集計
    const categories = ['ヘッドスパ', 'フェイシャル', 'アロマ', 'その他'];
    const categoryColors = {
      'ヘッドスパ': theme.palette.primary.main,
      'フェイシャル': theme.palette.secondary.main,
      'アロマ': theme.palette.success.main,
      'その他': theme.palette.warning.main,
    };

    const monthlyRevenue = new Map<string, Map<string, number>>();

    months.forEach(month => {
      const monthKey = format(month, 'yyyy-MM');
      const categoryMap = new Map<string, number>();
      categories.forEach(cat => categoryMap.set(cat, 0));
      monthlyRevenue.set(monthKey, categoryMap);
    });

    treatmentRecords.forEach(record => {
      const date = parseISO(record.treatmentDate);
      const monthKey = format(date, 'yyyy-MM');
      
      if (monthlyRevenue.has(monthKey)) {
        let category = 'その他';
        if (record.treatmentMenu.includes('ヘッドスパ')) {
          category = 'ヘッドスパ';
        } else if (record.treatmentMenu.includes('フェイシャル')) {
          category = 'フェイシャル';
        } else if (record.treatmentMenu.includes('アロマ')) {
          category = 'アロマ';
        }
        
        const categoryMap = monthlyRevenue.get(monthKey)!;
        categoryMap.set(category, (categoryMap.get(category) || 0) + record.totalAmount);
      }
    });

    const labels = months.map(month => 
      format(month, 'M月', { locale: ja })
    );

    const datasets = categories.map(category => ({
      label: category,
      data: months.map(month => {
        const monthKey = format(month, 'yyyy-MM');
        const categoryMap = monthlyRevenue.get(monthKey);
        const amount = categoryMap?.get(category) || 0;
        return amount / 10000; // 万円単位に変換
      }),
      backgroundColor: alpha(categoryColors[category as keyof typeof categoryColors], 0.8),
      borderColor: categoryColors[category as keyof typeof categoryColors],
      borderWidth: 2,
      borderRadius: 8,
      borderSkipped: false,
      barPercentage: 0.8,
      categoryPercentage: 0.9,
      hoverBackgroundColor: categoryColors[category as keyof typeof categoryColors],
    }));

    return {
      labels,
      datasets,
    };
  }, [treatmentRecords, theme]);

  const options: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            size: isMobile ? 11 : 12,
            family: theme.typography.fontFamily,
          },
          color: theme.palette.text.primary,
        },
      },
      tooltip: {
        enabled: true,
        backgroundColor: alpha(theme.palette.background.paper, 0.95),
        titleColor: theme.palette.text.primary,
        bodyColor: theme.palette.text.secondary,
        borderColor: theme.palette.divider,
        borderWidth: 1,
        padding: 12,
        displayColors: true,
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += '¥' + (context.parsed.y * 10000).toLocaleString();
            }
            return label;
          },
          footer: function(tooltipItems) {
            let sum = 0;
            tooltipItems.forEach(function(tooltipItem) {
              sum += tooltipItem.parsed.y;
            });
            return '合計: ¥' + (sum * 10000).toLocaleString();
          }
        }
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: {
          display: false,
        },
        ticks: {
          color: theme.palette.text.secondary,
          font: {
            size: isMobile ? 10 : 11,
          },
        },
      },
      y: {
        stacked: true,
        title: {
          display: !isMobile,
          text: '売上（万円）',
          color: theme.palette.text.secondary,
          font: {
            size: 11,
          },
        },
        grid: {
          color: alpha(theme.palette.divider, 0.3),
          display: true,
        },
        ticks: {
          color: theme.palette.text.secondary,
          font: {
            size: isMobile ? 10 : 11,
          },
          callback: function(value) {
            return value + '万';
          }
        },
      },
    },
    animation: {
      duration: 1500,
      easing: 'easeInOutQuart',
      delay: (context) => {
        let delay = 0;
        if (context.type === 'data' && context.mode === 'default') {
          delay = context.dataIndex * 100 + context.datasetIndex * 100;
        }
        return delay;
      },
    },
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5, delay: 0.2 }}
    >
      <Box sx={{ height: chartHeight, position: 'relative' }}>
        <Bar options={options} data={chartData} />
      </Box>
    </motion.div>
  );
};